// Mock data for Flight Delay Compensation landing page

export const heroData = {
  headline: "Get Up To €600 For Your Delayed Flight",
  subheadline: "Our AI powered platform checks your eligibility and submits your claim automatically. You only pay if compensation is recovered.",
  heroImage: "https://images.unsplash.com/photo-1771970668763-04a319bb5759"
};

export const trustSignals = [
  {
    title: "No Win, No Fee",
    description: "Only 25% + VAT if you receive compensation",
    icon: "shield-check"
  },
  {
    title: "Secure & GDPR Compliant",
    description: "Your data is protected and encrypted",
    icon: "lock"
  },
  {
    title: "Fast Check in 2 Minutes",
    description: "Get instant eligibility results",
    icon: "clock"
  },
  {
    title: "We Handle Paperwork",
    description: "No hassle, we do everything for you",
    icon: "file-text"
  }
];

export const howItWorksSteps = [
  {
    step: 1,
    title: "Enter Flight Details",
    description: "Tell us about your delayed or cancelled flight",
    icon: "plane"
  },
  {
    step: 2,
    title: "AI Checks Eligibility",
    description: "Our system validates your claim under EU261/UK261",
    icon: "brain-circuit"
  },
  {
    step: 3,
    title: "We Submit & You Get Paid",
    description: "Sit back while we handle the claim and get you compensated",
    icon: "coins"
  }
];

export const benefits = [
  {
    title: "Higher Success Rate",
    description: "AI-powered validation ensures maximum claim acceptance",
    icon: "trending-up"
  },
  {
    title: "Automated Document Collection",
    description: "We gather all necessary paperwork automatically",
    icon: "file-check"
  },
  {
    title: "Real-Time Status Updates",
    description: "Track your claim progress at every step",
    icon: "bell"
  },
  {
    title: "Multilingual Support",
    description: "Available in 12+ languages across Europe",
    icon: "globe"
  }
];

export const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "Business Traveler",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzIxMTU0NjJ8MA&ixlib=rb-4.1.0&q=85",
    content: "Got €600 compensation for my delayed London flight. The process was incredibly smooth and took just 3 weeks!",
    rating: 5,
    compensation: "€600"
  },
  {
    name: "Michael Chen",
    role: "Frequent Flyer",
    image: "https://images.unsplash.com/photo-1579047917338-a6a69144fe63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGN1c3RvbWVyfGVufDB8fHx8MTc3MjExNTQ2Nnww&ixlib=rb-4.1.0&q=85",
    content: "I didn't know I could claim for a 4-hour delay. Thanks to this service, I received €400 without any hassle.",
    rating: 5,
    compensation: "€400"
  },
  {
    name: "Amira Hassan",
    role: "Family Vacation",
    image: "https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    content: "Our family trip was delayed 6 hours. We received €2,400 in total (4 passengers). No upfront costs at all!",
    rating: 5,
    compensation: "€2,400"
  }
];

export const faqs = [
  {
    question: "Am I eligible for compensation?",
    answer: "You may be eligible if your flight was delayed 3+ hours, cancelled, or you were denied boarding. The flight must have departed from an EU/UK airport or arrived in the EU/UK on an EU airline within the last 6 years."
  },
  {
    question: "How long does the claim process take?",
    answer: "Most claims are processed within 4-8 weeks. Complex cases with airlines may take up to 12 weeks. We keep you updated throughout the entire process."
  },
  {
    question: "What documents do I need?",
    answer: "Just your booking reference and basic flight details. Our AI system automatically collects flight data, delay records, and other necessary documentation."
  },
  {
    question: "What is your success fee?",
    answer: "We charge 25% + VAT only if you receive compensation. If your claim is unsuccessful, you pay nothing. No upfront costs, no hidden fees."
  },
  {
    question: "Which airlines are covered?",
    answer: "We handle claims against all major airlines including Ryanair, EasyJet, British Airways, Lufthansa, Air France, KLM, and 200+ others operating under EU261/UK261 regulations."
  },
  {
    question: "What if the airline refuses to pay?",
    answer: "We escalate rejected claims to aviation authorities and legal partners. Our success rate is over 95% because we only pursue valid claims with strong evidence."
  }
];

export const supportedRegulations = [
  {
    title: "EU Regulation 261/2004",
    description: "Covers flights departing from EU airports",
    region: "European Union"
  },
  {
    title: "UK Regulation 261/2004",
    description: "Covers flights departing from UK airports",
    region: "United Kingdom"
  }
];

export const compensationRates = {
  short: { distance: "Up to 1,500 km", amount: 250 },
  medium: { distance: "1,500 - 3,500 km", amount: 400 },
  long: { distance: "Over 3,500 km", amount: 600 }
};

// Mock airlines data
export const airlines = [
  "Ryanair", "EasyJet", "British Airways", "Lufthansa", "Air France", 
  "KLM", "Wizz Air", "Vueling", "Norwegian", "Turkish Airlines",
  "Emirates", "Iberia", "TAP Portugal", "Alitalia", "Swiss Air",
  "Austrian Airlines", "SAS", "Finnair", "Aer Lingus", "Other"
];

// Mock function to calculate estimated compensation
export const calculateCompensation = (flightData) => {
  const { delayLength, distance } = flightData;
  
  // Delay must be 3+ hours
  if (delayLength < 3) {
    return { eligible: false, amount: 0, message: "Delay must be 3+ hours for compensation" };
  }
  
  // Calculate based on distance
  let amount = 0;
  if (distance <= 1500) {
    amount = 250;
  } else if (distance <= 3500) {
    amount = 400;
  } else {
    amount = 600;
  }
  
  return {
    eligible: true,
    amount,
    message: `You may be eligible for €${amount} compensation`,
    successFee: Math.round(amount * 0.25),
    yourPayout: Math.round(amount * 0.75)
  };
};
